'use client';

import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import { XCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useDarkMode } from '@/lib/platform/contexts/dark-mode-context';
import { PrimaryButton } from '@/components/platform/buttons';

interface ErrorModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  message?: string | React.ReactNode;
  buttonText?: string;
  showIcon?: boolean;
}

export default function ErrorModal({
  open,
  onClose,
  title,
  message,
  buttonText = 'Close',
  showIcon = true,
}: ErrorModalProps) {
  const { darkMode } = useDarkMode();

  return (
    <Dialog open={open} onClose={onClose} className="relative z-[60]">
      <DialogBackdrop
        transition
        className={`fixed inset-0 backdrop-blur-[2px] transition-opacity data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in ${
          darkMode ? 'bg-gray-900/75' : 'bg-gray-500/75'
        }`}
      />

      <div className="fixed inset-0 z-[60] w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <DialogPanel
            transition
            className={`relative transform overflow-hidden rounded-lg px-4 pt-5 pb-4 text-left shadow-xl transition-all data-closed:translate-y-4 data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in sm:my-8 sm:w-full sm:max-w-lg sm:p-6 data-closed:sm:translate-y-0 data-closed:sm:scale-95 ${
              darkMode ? 'border border-white/10 bg-gray-800' : 'bg-white'
            }`}
          >
            <div className="absolute top-0 right-0 hidden pt-4 pr-4 sm:block">
              <button
                type="button"
                onClick={onClose}
                className={`rounded-md focus:ring-2 focus:ring-red-500 focus:ring-offset-2 focus:outline-hidden ${
                  darkMode
                    ? 'bg-gray-800 text-gray-400 hover:text-gray-300'
                    : 'bg-white text-gray-400 hover:text-gray-500'
                }`}
              >
                <span className="sr-only">Close</span>
                <XMarkIcon aria-hidden="true" className="size-6" />
              </button>
            </div>

            <div className="sm:flex sm:items-start">
              {showIcon && (
                <div
                  className={`mx-auto flex size-16 shrink-0 items-center justify-center rounded-full sm:mx-0 sm:size-14 ${
                    darkMode ? 'bg-red-900/20' : 'bg-red-100'
                  }`}
                >
                  <XCircleIcon
                    aria-hidden="true"
                    className={`size-10 ${darkMode ? 'text-red-400' : 'text-red-600'}`}
                  />
                </div>
              )}
              <div className={`mt-3 text-center ${showIcon ? 'sm:mt-0 sm:ml-4' : ''} sm:text-left`}>
                <DialogTitle
                  as="h3"
                  className={`text-base font-semibold ${
                    darkMode ? 'text-gray-100' : 'text-gray-900'
                  }`}
                >
                  {title}
                </DialogTitle>
                {message && (
                  <div className="mt-2">
                    {typeof message === 'string' ? (
                      <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {message}
                      </p>
                    ) : (
                      <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {message}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="mt-5 flex justify-center sm:mt-4 sm:justify-end">
              <PrimaryButton onClick={onClose} className="w-full sm:w-auto">
                {buttonText}
              </PrimaryButton>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}
